// ---------------------------------------------------------------------------
// System health indicator — traffic light with component breakdown
// ---------------------------------------------------------------------------

import { useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy';

export interface ComponentHealth {
  name: string;
  status: HealthStatus;
  latencyMs: number;
  error?: string;
}

interface SystemHealthIndicatorProps {
  overall: HealthStatus;
  components: ComponentHealth[];
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<HealthStatus, { color: string; ringColor: string; label: string }> = {
  healthy: { color: 'bg-primary-tint0', ringColor: 'ring-primary/30', label: 'All Systems Operational' },
  degraded: { color: 'bg-warning', ringColor: 'ring-warning/30', label: 'Partial Degradation' },
  unhealthy: { color: 'bg-danger-tint0', ringColor: 'ring-danger/30', label: 'System Issues Detected' },
};

const COMPONENT_DOT: Record<HealthStatus, string> = {
  healthy: 'bg-primary-tint0',
  degraded: 'bg-warning',
  unhealthy: 'bg-danger-tint0',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SystemHealthIndicator({ overall, components }: SystemHealthIndicatorProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const config = STATUS_CONFIG[overall];

  return (
    <div className="relative">
      {/* Traffic light indicator */}
      <button
        type="button"
        onClick={() => setShowBreakdown(!showBreakdown)}
        onMouseEnter={() => setShowBreakdown(true)}
        onMouseLeave={() => setShowBreakdown(false)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-surface-raised border border-hairline hover:border-hairline-strong transition-colors"
        aria-label={`System health: ${config.label}`}
      >
        <span className={`w-2.5 h-2.5 rounded-sm ${config.color} ring-2 ${config.ringColor}`} />
        <span className="text-xs font-medium text-ink">{config.label}</span>
      </button>

      {/* Breakdown popover */}
      {showBreakdown && components.length > 0 && (
        <div className="absolute top-full end-0 mt-2 w-72 bg-surface-raised rounded-sm shadow-md border border-hairline py-3 z-50">
          <div className="px-4 pb-2 mb-2 border-b border-hairline">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider">
              Component Status
            </p>
          </div>
          <div className="space-y-0.5">
            {components.map((c) => (
              <div key={c.name} className="flex items-center justify-between px-4 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-2 h-2 rounded-sm flex-shrink-0 ${COMPONENT_DOT[c.status]}`} />
                  <span className="text-sm text-ink truncate">{c.name}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {c.latencyMs > 0 && (
                    <span className="text-xs text-ink-muted">{c.latencyMs}ms</span>
                  )}
                  <span className="text-xs text-ink-muted capitalize">{c.status}</span>
                </div>
              </div>
            ))}
          </div>
          {components.some((c) => c.error) && (
            <div className="mt-2 pt-2 px-4 border-t border-hairline">
              {components
                .filter((c) => c.error)
                .map((c) => (
                  <p key={c.name} className="text-xs text-danger mt-1">
                    {c.name}: {c.error}
                  </p>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
